class ApplicationModule extends HTMLElement {
  constructor() {
    super();

    this.form_controls = this.querySelector(".application-form");
    this.form = this.querySelector("form");
    this.navigation = this.querySelector(".application__navigation");
    this.submit_button = this.querySelector("[data-submit]");
    this.storage_key = this.dataset.storageKey || "application_form_data";
    this.conditional_groups = Array.from(this.querySelectorAll("[data-conditional-logic]"))?.map(
      (group) => {
        return group.dataset.conditionalId;
      },
    );
    this.stored_data = window.localStorage.getItem(this.storage_key)
      ? JSON.parse(window.localStorage.getItem(this.storage_key))
      : this.getDefaultStoredData();
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: this.form_controls,
        type: "change",
        callback: this.changeHandler.bind(this),
      },
      {
        el: this.form,
        type: "submit",
        callback: this.submitHandler.bind(this),
      },
    ];
    if (Shopify.designMode) {
      this.events.push({
        el: document,
        type: "shopify:block:select",
        callback: this.focusPage.bind(this),
      });
    }
  }
  connectedCallback() {
    handleEventListeners(this.events, "add");
    this.init();
  }
  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  init() {
    this.initFormInputs();
    this.setConditionalFieldState();
    const current_index = this.stored_data.current_page;
    const total_pages = +this.querySelector("[data-page]:last-child").dataset.page;
    this.current_page = this.querySelector(`[data-page="${current_index}"]`);
    this.current_page.setAttribute("data-current-page", true);
    this.populateFormData();
    this.setUploadsState();
    for (let i = 0; i <= total_pages; i++) {
      const page = this.form_controls.querySelector(`[data-page="${i}"]`);
      this.checkPageValidation(page);
    }
    if (current_index !== 0) {
      this.transitionPage();
    }
  }

  getLogicalInputName(input) {
    return input.name.replace(/^multiselect_\d+_/, "");
  }

  getSourceFormControls() {
    return Array.from(this.form_controls.querySelectorAll("input, select")).filter((input) => {
      return input.dataset.formInput !== "true" && !this.form.contains(input);
    });
  }

  getFormControlsByName(name) {
    return this.getSourceFormControls().filter((input) => {
      return this.getLogicalInputName(input) === name;
    });
  }

  getFormControlByName(name) {
    return this.getFormControlsByName(name)[0];
  }

  getSyncTargetsByName(name) {
    return Array.from(this.querySelectorAll("[data-sync-id], [data-sync-ids]")).filter((target) => {
      const sync_ids = target.dataset.syncIds ? target.dataset.syncIds.split("|||") : [];
      return target.dataset.syncId === name || sync_ids.includes(name);
    });
  }

  initFormInputs() {
    const input_names = this.getSourceFormControls().map((input) => {
      return this.getLogicalInputName(input);
    });
    const unique_input_names = [...new Set(input_names)];
    for (const name of unique_input_names) {
      if (!name) continue;

      const input = document.createElement("input");
      input.name = name;
      input.dataset.formInput = "true";
      const existing_input = this.getFormControlByName(name);
      if (existing_input?.dataset.syncId) {
        input.dataset.syncId = existing_input.dataset.syncId;
      } else {
        input.dataset.syncId = name;
      }
      input.type = "hidden";
      this.form.appendChild(input);
    }
  }

  clickHandler(e) {
    if (e.target.dataset.disabled === "true") {
      this.checkPageValidation(this.current_page, true);
    } else if (e.target.dataset.pageTarget) {
      this.transitionPage(e);
    }
  }

  changeHandler(e) {
    if (this.conditional_groups.includes(e.target.name)) {
      this.handleConditional(e.target);
      this.storeFormState();
      this.checkPageValidation(this.current_page);
    } else {
      const { is_valid, target } = this.checkInputValidation(e.target);
      this.validateField(is_valid, target);
      this.storeFormState();
      this.checkPageValidation(this.current_page);
    }
  }

  submitHandler() {
    this.clearStoredFormState();
    this.submit_button?.setAttribute("disabled", "disabled");
  }

  getDefaultStoredData() {
    return {
      current_page: 0,
      data: {},
    };
  }

  clearStoredFormState() {
    window.localStorage.removeItem(this.storage_key);
    this.stored_data = this.getDefaultStoredData();
  }

  setConditionalFieldState() {
    const groups = this.querySelectorAll("[data-conditional-logic]");
    for (const group of groups) {
      if (group.getAttribute("aria-hidden") === "true") {
        for (const input of group.querySelectorAll("input,select")) {
          input.closest("[data-valid]").setAttribute("data-ignore", "true");
          input.setAttribute("disabled", "disabled");
        }
      }
    }
  }
  handleConditional(target) {
    const conditional_group = this.querySelector(`[data-conditional-id="${target.name}"]`);
    const logic = conditional_group.dataset.conditionalLogic;

    let group_active;
    if (
      (logic === "true" && target.checked) ||
      (logic === "false" && !target.checked) ||
      (logic === "empty" && target.value === "") ||
      (logic === "filled" && target.value !== "")
    ) {
      conditional_group.setAttribute("aria-hidden", "false");
      group_active = true;
    } else {
      conditional_group.setAttribute("aria-hidden", "true");
      group_active = false;
    }
    for (const field of conditional_group.querySelectorAll("input,select")) {
      if (!group_active) {
        field.closest("[data-valid]").setAttribute("data-ignore", "true");
        field.setAttribute("disabled", "disabled");
        field.value = "";
        this.syncField(field);
      } else {
        field.closest("[data-valid]").removeAttribute("data-ignore");
        field.removeAttribute("disabled");
      }
    }
    this.syncField(target);
  }

  setUploadsState() {
    const upload_buttons = this.form_controls.querySelectorAll("[data-valid-query]");
    for (const button of upload_buttons) {
      const valid_query = button.dataset.validQuery;
      const input = button.closest(".application__field").querySelector("input");
      if (
        window.location.search.includes(`${valid_query}=true`) ||
        this.stored_data.data[input.name] === "Uploaded"
      ) {
        button.closest(".application__field").dataset.valid = "true";
        input.value = "Uploaded";
        this.syncField(input);
        this.storeFormState();
      }
    }
  }

  checkInputValidation(target) {
    let is_valid = false;

    target.dataset.value = target.value;

    this.syncField(target);

    if (target.closest("[data-required]").dataset.required === "false") {
      is_valid = true;
    } else {
      if (target.name.includes("multiselect_")) {
        const buttons = target.closest(".field__buttons").querySelectorAll("input");
        let threshold_met = false;
        for (const button of buttons) {
          if (button.checked) {
            threshold_met = true;
            break;
          }
        }
        is_valid = threshold_met;
      } else if (
        (target.type === "radio" && target.checked) ||
        (target.type === "checkbox" && target.checked) ||
        ((target.type === "text" || target.type.includes("select")) && target.value.length > 0)
      ) {
        is_valid = true;
      }
    }

    return { is_valid, target };
  }

  validateField(is_valid, input) {
    const field = input.closest(".application__field");
    field.dataset.valid = is_valid;
    if (is_valid) {
      field.querySelectorAll(".field__invalid").forEach((field_input) => {
        field_input.classList.remove("field__invalid");
      });
    } else {
      input.classList.add("field__invalid");
    }
  }

  syncField(target) {
    const target_name = this.getLogicalInputName(target);
    const sync_targets = this.getSyncTargetsByName(target_name);
    for (const sync_target of sync_targets) {
      let value = target.value;
      let sync_input_names;
      if (sync_target.dataset.syncIds?.split("|||").length > 0) {
        sync_input_names = sync_target.dataset.syncIds.split("|||");
      } else {
        if (sync_target.dataset.syncIds) {
          sync_input_names = [sync_target.dataset.syncIds];
        } else {
          sync_input_names = [sync_target.dataset.syncId];
        }
      }
      const data = [];
      for (const sync_input_name of sync_input_names) {
        const source = this.getFormControlsByName(sync_input_name);
        for (const source_input of source) {
          if (source_input.type === "checkbox" || source_input.type === "radio") {
            if (source_input.checked) {
              data.push(source_input.value);
            }
          } else {
            data.push(source_input.value);
          }
        }
      }
      value = data.filter((val) => val !== "").join(", ");

      if (sync_target.tagName !== "SPAN") {
        sync_target.value = value;
      } else {
        if (value === "") {
          sync_target.innerHTML = "&horbar;";
        } else {
          sync_target.innerText = value;
        }
      }
    }
  }

  storeFormState() {
    const form_data = new FormData(this.form);
    const data = {};
    for (const [key, value] of form_data) {
      data[key] = value;
    }
    this.stored_data.data = data;
    window.localStorage.setItem(this.storage_key, JSON.stringify(this.stored_data));
  }

  populateFormData() {
    for (const [key, value] of Object.entries(this.stored_data.data)) {
      let form_input = this.getFormControlByName(key);
      if (!form_input) continue;

      if (form_input.name.includes("multiselect_")) {
        const stored_value = value.split(", ");
        const form_inputs = this.getFormControlsByName(key);
        for (const stored_val of stored_value) {
          form_input = form_inputs.find((input) => input.value === stored_val);
          if (form_input) {
            form_input.checked = true;
          }
        }
      } else if (form_input.type === "checkbox") {
        if (value === "true") {
          form_input.checked = true;
        }
      } else if (form_input.type === "text") {
        form_input.value = value;
        form_input.dataset.value = value;
      } else if (form_input.type === "radio") {
        form_input = this.getFormControlsByName(key).find((input) => input.value === value);
        if (form_input) {
          form_input.checked = true;
        }
      } else if (form_input.type.includes("select")) {
        for (const option of form_input.options) {
          if (option.value === value) {
            option.selected = true;
          }
        }
      }

      if (form_input) {
        if (this.querySelector(`[data-conditional-id="${form_input.name}"]`)) {
          this.handleConditional(form_input);
        }

        if (value !== "" && form_input.type !== "hidden") {
          const { is_valid, target } = this.checkInputValidation(form_input);
          this.validateField(is_valid, target);
        }
      }
    }
  }

  focusInvalidFields(invalid_fields) {
    for (const field of invalid_fields) {
      if (field.querySelector(".btn-elem")) {
        field.querySelector(".btn-elem").classList.add("field__invalid");
      } else {
        field.querySelector("input,select").classList.add("field__invalid");
      }
    }
    this.setScrollPosition(invalid_fields[0]);
  }

  checkPageValidation(page, focus = false) {
    const invalid_fields = page.querySelectorAll('[data-valid="false"]:not([data-ignore="true"])');
    let page_valid = true;
    if (invalid_fields.length > 0) {
      page_valid = false;
      if (focus) {
        this.focusInvalidFields(invalid_fields);
      }
    }
    if (!page_valid) {
      page.querySelector("[data-next]").setAttribute("data-disabled", true);
    } else {
      page.querySelector("[data-next]").removeAttribute("data-disabled");
    }
  }

  transitionPage(e) {
    const page_index = e?.target.dataset.pageTarget || this.stored_data.current_page;
    const target_page = this.querySelector(`[data-page="${page_index}"]`);
    this.current_page.removeAttribute("data-current-page");
    target_page.setAttribute("data-current-page", true);
    this.current_page = target_page;
    this.stored_data.current_page = +page_index;
    this.setScrollPosition(this);
    this.updateProgress();
    this.storeFormState();
  }
  focusPage(e) {
    const page = e.target.closest(".application__page");
    if (!page) return;

    const page_index = page.dataset.page;
    const target_page = this.querySelector(`[data-page="${page_index}"]`);
    this.current_page.removeAttribute("data-current-page");
    target_page.setAttribute("data-current-page", true);
    this.current_page = target_page;
    this.updateProgress();
    this.setScrollPosition(e.target);
  }
  setScrollPosition(target) {
    const modal_body =
      this.closest("[data-trade-application-modal-scroll-container]") ||
      this.closest("[data-trade-application-modal-body]");
    if (modal_body) {
      const body_rect = modal_body.getBoundingClientRect();
      const target_rect = target.getBoundingClientRect();
      modal_body.scrollTo({
        top: modal_body.scrollTop + target_rect.top - body_rect.top - 20,
        behavior: "smooth",
      });
      return;
    }

    const body_rect = document.body.getBoundingClientRect();
    const target_rect = target.getBoundingClientRect();
    const header_offset =
      document.querySelector("site-header")?.getBoundingClientRect().height || 0;
    window.scrollTo({
      top: target_rect.top - body_rect.top - header_offset - 50,
      behavior: "smooth",
    });
  }
  updateProgress() {
    // Update step number
    const page_number = +this.current_page.dataset.page + 1;
    this.querySelector("[data-current-step]").innerText = page_number;

    // Update % complete
    const total_pages = +this.querySelector("[data-steps]").dataset.steps;
    const percentage_complete = Math.round(((page_number - 1) / (total_pages - 1)) * 100);
    this.querySelector("[data-percentage]").innerText = percentage_complete;

    // Change current step title
    this.querySelector("[data-step-title][data-current]").removeAttribute("data-current");
    this.querySelector(`[data-step-title="${page_number}"]`).setAttribute("data-current", true);

    // Move progress bar
    this.querySelector("[data-progress]").style.width = `${percentage_complete}%`;
  }
}
if (!customElements.get("application-module")) {
  customElements.define("application-module", ApplicationModule);
}
